/**
 * The cross-tab announcement seam for a completed entity switch (DV-3013 spec
 * §3.6, trigger 1).
 *
 * Deliberately imports NOTHING from the app. `entity-scope.ts` needs the
 * publish half from inside the switch sequence, and an import that reached
 * `entity-claim -> callBack.service` from there would add an edge to the graph
 * `get-store.ts` exists to keep clear (spec §2.4 / §3.8).
 *
 * The channel is a HINT, never the authority: `reconcileScope()` re-reads the
 * token claim on every trigger, so a dropped or duplicated message costs
 * nothing. That is why nothing is carried in the payload — a message says only
 * "look again".
 */

const CHANNEL_NAME = 'entity_scope';

/**
 * Lazily constructed, and absent where the API is not available (older
 * Safari, some embedded webviews). The `storage` and `visibilitychange`
 * triggers cover that tab; spec §3.6's table is explicit that no single
 * trigger has to be reliable.
 */
let channel: BroadcastChannel | null | undefined;

const getChannel = (): BroadcastChannel | null => {
  if (channel !== undefined) return channel;
  channel = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(CHANNEL_NAME);
  return channel;
};

/** Called by `switchEntity` once the new scope is live in this tab. */
export const announceScopeChange = (): void => {
  try {
    getChannel()?.postMessage('scope-changed');
  } catch {
    // A closed or unavailable channel must never fail a switch that has
    // already succeeded. The other two triggers still catch it.
  }
};

/** Returns an unsubscribe fn. */
export const subscribeScopeAnnouncements = (onAnnouncement: () => void): (() => void) => {
  const active = getChannel();
  if (!active) return () => {};
  const handler = () => onAnnouncement();
  active.addEventListener('message', handler);
  return () => active.removeEventListener('message', handler);
};

/**
 * Test-only. Closes the memoised channel (if any) and drops the reference so
 * a fresh one is built on next use — otherwise a channel opened by one test
 * stays subscribed and can receive stray messages from the next.
 */
export const __resetScopeBroadcastForTests = (): void => {
  channel?.close();
  channel = undefined;
};

/**
 * In-tab signal: THIS tab just wrote a new access token (DV-3421 review round
 * 5, item 1).
 *
 * <p>A `storage` event reaches every tab EXCEPT the one that wrote, so a tab
 * whose own refresh brought back a moved token — an admin added the user to a
 * second company, say, and the next proactive refresh picked up the new root —
 * would otherwise not look again until the next focus or visibility change.
 * A DOM event rather than a direct call so `auth-token` does not import the
 * reconciler.
 */
const TOKEN_WRITTEN_EVENT = 'planomatic:access-token-written';

export const notifyTokenWrittenHere = (): void => {
  try {
    window.dispatchEvent(new Event(TOKEN_WRITTEN_EVENT));
  } catch {
    // A listener's failure must never fail the refresh that triggered it.
  }
};

export const subscribeTokenWrittenHere = (onWrite: () => void): (() => void) => {
  const handler = () => onWrite();
  window.addEventListener(TOKEN_WRITTEN_EVENT, handler);
  return () => window.removeEventListener(TOKEN_WRITTEN_EVENT, handler);
};
